import { useCallback, useEffect, useRef } from 'react';

/**
 * 画面に開きっぱなしのタブが同じ API を叩き続けないためのポーリング共通処理。
 *
 * - タブが非表示の間は止め、表示に戻ったら 1 回取得してから再開する。
 * - 401/402/403/404 は待っても直らないので、そのインスタンスのポーリングを止める。
 * - 通信断・5xx などの一時エラーは間隔を倍々に延ばし (上限 maxBackoffMs)、成功で戻す。
 * - axiosConfig が `trial_expired` を通知したら、リロードまで全インスタンスの定期取得を止める。
 *
 * 本番ログでは、28 日開いたままのタブ 1 つで 2,482 件、トライアル切れのモーダル裏で
 * 403 を数千件出し続けていた。
 */

const PERMANENT_ERROR_STATUSES = new Set([401, 402, 403, 404]);
const DEFAULT_MAX_BACKOFF_MS = 60_000;

let haltedByTrialExpired = false;
if (typeof window !== 'undefined') {
  window.addEventListener('trial_expired', () => {
    haltedByTrialExpired = true;
  });
}

export const getErrorStatus = (error: unknown): number | undefined => {
  const e = error as { response?: { status?: unknown }; status?: unknown } | null | undefined;
  const status = e?.response?.status ?? e?.status;
  return typeof status === 'number' ? status : undefined;
};

export const isPermanentPollingError = (error: unknown): boolean => {
  const status = getErrorStatus(error);
  return status !== undefined && PERMANENT_ERROR_STATUSES.has(status);
};

const isHidden = () => typeof document !== 'undefined' && document.visibilityState === 'hidden';

export interface UsePollingOptions {
  /** true の間だけ定期取得する (例: 進行中のジョブがあるとき)。 */
  enabled: boolean;
  intervalMs: number;
  maxBackoffMs?: number;
  /** enabled=false でも、タブが表示に戻ったときに 1 回取得する。 */
  refreshOnVisibleWhileIdle?: boolean;
}

/**
 * @param load 取得処理。失敗時は例外を投げること (ステータスで停止・バックオフを決める)。
 * @returns run: 定期取得と同じ扱いで 1 回取得する。初回取得やイベント起点の再取得に使う。
 *   例外は握りつぶしてログに出すだけなので、呼び出し側で catch しなくてよい。
 */
export const usePolling = (load: () => Promise<unknown>, options: UsePollingOptions) => {
  const { enabled, intervalMs, maxBackoffMs = DEFAULT_MAX_BACKOFF_MS, refreshOnVisibleWhileIdle = false } = options;
  const loadRef = useRef(load);
  loadRef.current = load;
  const failuresRef = useRef(0);
  const stoppedRef = useRef(false);
  // 停止中に明示的な取得が成功したとき、定期取得を張り直すために effect から受け取る。
  const rearmRef = useRef<() => void>(() => {});

  // 明示的な呼び出しは止まっていても実行する。画面を開き直したときの 1 回で
  // 403 trial_expired が返れば、モーダルが改めて出る。
  const run = useCallback(async () => {
    try {
      await loadRef.current();
      failuresRef.current = 0;
      const wasStopped = stoppedRef.current;
      stoppedRef.current = false;
      if (wasStopped) rearmRef.current();
    } catch (error) {
      if (isPermanentPollingError(error)) {
        stoppedRef.current = true;
      } else {
        failuresRef.current += 1;
      }
      console.error('Polling request failed:', error);
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;
    let cancelled = false;
    let running = false;
    let timer: ReturnType<typeof setTimeout> | null = null;

    const clear = () => {
      if (timer) {
        clearTimeout(timer);
        timer = null;
      }
    };
    const schedule = () => {
      clear();
      if (cancelled || stoppedRef.current || haltedByTrialExpired || isHidden()) return;
      const delay = failuresRef.current > 0
        ? Math.min(intervalMs * 2 ** failuresRef.current, Math.max(maxBackoffMs, intervalMs))
        : intervalMs;
      timer = setTimeout(tick, delay);
    };
    const tick = async () => {
      timer = null;
      if (running || cancelled || stoppedRef.current || haltedByTrialExpired) return;
      running = true;
      await run();
      running = false;
      schedule();
    };
    const onVisibilityChange = () => {
      clear();
      if (!isHidden()) void tick();
    };
    const onTrialExpired = () => clear();
    rearmRef.current = () => {
      if (!running && !timer) schedule();
    };

    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('trial_expired', onTrialExpired);
    schedule();
    return () => {
      cancelled = true;
      clear();
      rearmRef.current = () => {};
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('trial_expired', onTrialExpired);
    };
  }, [enabled, intervalMs, maxBackoffMs, run]);

  useEffect(() => {
    if (enabled || !refreshOnVisibleWhileIdle) return;
    const onVisibilityChange = () => {
      if (isHidden() || stoppedRef.current || haltedByTrialExpired) return;
      void run();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => document.removeEventListener('visibilitychange', onVisibilityChange);
  }, [enabled, refreshOnVisibleWhileIdle, run]);

  return { run };
};

export default usePolling;
